import {
  parsePhoneNumberWithError,
  validatePhoneNumberLength,
} from "libphonenumber-js";

/**
 * Gets a user-friendly error message based on a phone validation error code.
 *
 * @param {string} errorCode - The error code provided by the validator.
 * @returns {string} The error message.
 */
export const getPhoneErrorMessage = (errorCode) => {
  if (errorCode === "INVALID_COUNTRY") {
    return "Código de país faltante o inválido";
  }

  if (errorCode === "NOT_A_NUMBER") {
    return "No es un número válido";
  }

  if (errorCode === "TOO_SHORT") {
    return "Le faltan dígitos al número";
  }

  if (errorCode === "TOO_LONG") {
    return "El número es muy largo";
  }

  if (errorCode === "INVALID_LENGTH") {
    return "Longitud inválida";
  }

  return "Numero inválido";
};

/**
 * Formats a phone number to international E.164 format
 * @param {string} phoneNumber - Phone number to format
 * @param {string} defaultCountry - Default country code
 * @returns {{number: string|null, error: string|null, country: string|null, format: string|null}}
 */
export const formatPhoneNumber = (phoneNumber, defaultCountry) => {
  if (!phoneNumber) return { number: null, error: "Número vacío" };

  try {
    const parsed = parsePhoneNumberWithError(phoneNumber, defaultCountry);

    if (parsed && parsed.isValid()) {
      return {
        number: parsed.number,
        country: parsed.country,
        error: null,
        format: parsed.formatInternational(),
      };
    }

    const internationalParse = parsePhoneNumberWithError("+" + phoneNumber);

    if (internationalParse && internationalParse.isValid()) {
      return {
        number: internationalParse.number,
        country: internationalParse.country,
        error: null,
        format: internationalParse.formatInternational(),
      };
    }

    const errorReason = validatePhoneNumberLength(parsed.number);

    throw new Error(errorReason);
  } catch (error) {
    return {
      number: null,
      country: null,
      error: getPhoneErrorMessage(error.message),
      format: null,
    };
  }
};

/**
 * Formats a phone number for WhatsApp, handling country-specific prefixes
 * and removing the '+' symbol.
 * @param {string} phoneNumber - Phone number in E.164 format
 * @returns {string} Formatted number for the WhatsApp API
 */
export const formatPhoneNumberForWhatsApp = (phoneNumber) => {
  let newPhone = phoneNumber;

  // Mexico
  if (phoneNumber.startsWith("+52") && !phoneNumber.startsWith("+521")) {
    newPhone = phoneNumber.replace("+52", "+521");
  }

  // Argentina
  if (phoneNumber.startsWith("+54") && !phoneNumber.startsWith("+549")) {
    newPhone = phoneNumber.replace("+54", "+549");
  }

  return newPhone.replace("+", "");
};

/**
 * Formats a phone number from WhatsApp format back to E.164 format,
 * handling country-specific prefixes and adding back the '+' symbol.
 * @param {string} phoneNumber - Phone number without '+' (WhatsApp format)
 * @returns {string} Formatted number in E.164 format
 */
export const formatPhoneNumberFromWhatsApp = (phoneNumber) => {
  let newPhone = phoneNumber;

  // Mexico - remove the extra '1' to convert 521 back to 52
  if (phoneNumber.startsWith("521") || phoneNumber.startsWith("+521")) {
    newPhone = phoneNumber.replace("521", "+52");
  }

  // Argentina - remove the extra '9' to convert 549 back to 54
  if (phoneNumber.startsWith("549") || phoneNumber.startsWith("+549")) {
    newPhone = phoneNumber.replace("549", "+54");
  }

  return newPhone;
};
